import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  User,
  MapPin,
  Camera,
  PenTool,
  X,
  ArrowRight,
  Info,
  Lock,
} from "lucide-react";

import {
  useGetSectorsQuery,
  useGetStatesQuery,
  useGetLocalGovernmentsQuery,
  useGetChaptersQuery,
} from "../../api/probationerApplicationApi";
import { useAuth } from "../../../../context/AuthContexts";

export type CategoryLite = {
  id: number;
  name: string;
  is_active?: boolean;
};

export type Step1BioInitial = {
  surname: string;
  other_names: string;
  title: string;
  gender?: string;
  postal_address?: string;
  residential_address: string;
  email?: string;
  phone: string;
  date_of_birth: string;
  nationality: string;
  application_category_id?: number | null;
  sector_id?: number | null;
  state_id?: number | null;
  local_government_id?: number | null;
  chapter_id?: number | null | string;
  chapter_other?: string;
  pro_pic_url?: string;
  signature_url?: string;
};

const GENDER_OPTIONS = [
  { id: "male", name: "Male" },
  { id: "female", name: "Female" },
];

export default function Step1Bio({
  onSubmit,
  busy,
  initialValues,
  categories,
  readOnly,
  upgradeToCategoryName,
}: {
  busy: boolean;
  categories: CategoryLite[];
  readOnly?: boolean;
  initialValues?: Step1BioInitial | null;
  onSubmit: (payload: any) => Promise<void>;
  // When set, this is an upgrade: show the current grade + the grade being
  // upgraded to, both locked (derived from the user's category, not chosen).
  upgradeToCategoryName?: string | null;
}) {
  const empty = useMemo(
    () => ({
      surname: "",
      other_names: "",
      title: "",
      gender: "",
      postal_address: "",
      residential_address: "",
      date_of_birth: "",
      nationality: "Nigerian",
      sector_id: "",
      state_id: "",
      local_government_id: "",
      chapter_id: "",
      chapter_other: "",
    }),
    [],
  );

  const [form, setForm] = useState(empty);
  const [proPic, setProPic] = useState<File | null>(null);
  const [signature, setSignature] = useState<File | null>(null);
  const [proPicPreview, setProPicPreview] = useState<string | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [compressing, setCompressing] = useState<"photo" | "signature" | null>(
    null,
  );

  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!initialValues || hydratedRef.current) return;
    hydratedRef.current = true;

    setForm({
      surname: initialValues.surname ?? "",
      other_names: initialValues.other_names ?? "",
      title: initialValues.title ?? "",
      gender: initialValues.gender ?? "",
      postal_address: initialValues.postal_address ?? "",
      residential_address: initialValues.residential_address ?? "",
      date_of_birth: initialValues.date_of_birth ?? "",
      nationality: initialValues.nationality ?? "Nigerian",
      sector_id: initialValues.sector_id ? String(initialValues.sector_id) : "",
      state_id: initialValues.state_id ? String(initialValues.state_id) : "",
      local_government_id: initialValues.local_government_id
        ? String(initialValues.local_government_id)
        : "",
      chapter_id: initialValues.chapter_id
        ? String(initialValues.chapter_id)
        : "",
      chapter_other: initialValues.chapter_other ?? "",
    });
  }, [initialValues]);

  useEffect(() => {
    if (!proPic) {
      setProPicPreview(null);
      return;
    }
    const url = URL.createObjectURL(proPic);
    setProPicPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [proPic]);

  useEffect(() => {
    if (!signature) {
      setSignaturePreview(null);
      return;
    }
    const url = URL.createObjectURL(signature);
    setSignaturePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [signature]);

  const onChange = (k: string, v: string) => setForm((s) => ({ ...s, [k]: v }));

  const { data: sectors = [] } = useGetSectorsQuery();
  const { data: states = [] } = useGetStatesQuery();
  const { data: localGovernmentsRaw = [] } = useGetLocalGovernmentsQuery(
    Number(form.state_id),
    { skip: !form.state_id },
  );
  const localGovernments = Array.isArray(localGovernmentsRaw)
    ? localGovernmentsRaw
    : [];
  const { data: chaptersRaw = [] } = useGetChaptersQuery();

  // Append "Others" as the last chapter option
  const chapters = useMemo(
    () => [
      ...(Array.isArray(chaptersRaw) ? chaptersRaw : []),
      { id: "__other__", name: "Others" },
    ],
    [chaptersRaw],
  );

  const isOtherChapter = form.chapter_id === "__other__";

  // Locked fields sourced from the logged-in user — never editable
  const { user: authUser } = useAuth();
  const authEmail = authUser?.email ?? "";
  const authPhone = authUser?.phone ?? authUser?.phone_number ?? "";
  const authCategoryId = authUser?.user_category_id ?? null;
  const authCategoryName = useMemo(
    () => categories.find((c) => c.id === Number(authCategoryId))?.name ?? "—",
    [categories, authCategoryId],
  );

  // Compresses an image to fit within maxBytes. Returns File on success or an
  // error string if the source image is below the required minimum dimensions.
  const autoCompress = (
    file: File,
    maxBytes: number,
    minW: number,
    minH: number,
    label: string,
  ): Promise<File | string> =>
    new Promise((resolve) => {
      const allowed = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowed.includes(file.type)) {
        resolve(`${label} must be a JPG or PNG file.`);
        return;
      }

      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        try {
          URL.revokeObjectURL(url);

          if (img.naturalWidth < minW || img.naturalHeight < minH) {
            resolve(
              `${label} must be at least ${minW}×${minH}px (yours is ${img.naturalWidth}×${img.naturalHeight}px).`,
            );
            return;
          }

          // Already within limits — return as-is
          if (file.size <= maxBytes) {
            resolve(file);
            return;
          }

          // Scale down proportionally so the longest edge ≤ 2500px,
          // but never below the required minimums.
          const MAX_PX = 2500;
          let w = img.naturalWidth;
          let h = img.naturalHeight;
          if (w > MAX_PX || h > MAX_PX) {
            const ratio = Math.min(MAX_PX / w, MAX_PX / h);
            w = Math.max(minW, Math.round(w * ratio));
            h = Math.max(minH, Math.round(h * ratio));
          }

          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) { resolve(file); return; }
          ctx.drawImage(img, 0, 0, w, h);

          const attempt = (quality: number) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) { resolve(file); return; }
                if (blob.size <= maxBytes || quality <= 0.2) {
                  resolve(
                    new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
                      type: "image/jpeg",
                      lastModified: Date.now(),
                    }),
                  );
                } else {
                  attempt(Math.max(0.2, quality - 0.1));
                }
              },
              "image/jpeg",
              quality,
            );
          };

          attempt(0.85);
        } catch {
          resolve(file);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file);
      };

      try {
        img.src = url;
      } catch {
        URL.revokeObjectURL(url);
        resolve(file);
      }
    });

  const handleProPicSelect = async (file: File | null) => {
    setError(null);
    if (!file) { setProPic(null); return; }
    setCompressing("photo");
    try {
      const result = await autoCompress(file, 2 * 1024 * 1024, 300, 300, "Passport photograph");
      setCompressing(null);
      if (typeof result === "string") { setError(result); setProPic(null); }
      else setProPic(result);
    } catch {
      setCompressing(null);
      setError("Image could not be processed. Please try selecting the file again.");
      setProPic(null);
    }
  };

  const handleSignatureSelect = async (file: File | null) => {
    setError(null);
    if (!file) { setSignature(null); return; }
    setCompressing("signature");
    try {
      const result = await autoCompress(file, 1 * 1024 * 1024, 600, 200, "Signature");
      setCompressing(null);
      if (typeof result === "string") { setError(result); setSignature(null); }
      else setSignature(result);
    } catch {
      setCompressing(null);
      setError("Image could not be processed. Please try selecting the file again.");
      setSignature(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (busy || compressing) return;

    if (!authPhone.trim()) {
      setError(
        "Your account has no phone number. Please update your profile first, then return to complete this step.",
      );
      return;
    }

    // Validate all required fields
    const required: [string, string][] = [
      [form.title, "Title"],
      [form.surname, "Surname"],
      [form.other_names, "Other Names"],
      [form.gender, "Gender"],
      [form.date_of_birth, "Date of Birth"],
      [form.nationality, "Nationality"],
      [form.sector_id, "Professional Sector"],
      [form.state_id, "State of Residence"],
      [form.local_government_id, "Local Government"],
      [form.chapter_id, "Chapter"],
      [form.residential_address, "Residential Address"],
    ];

    for (const [val, label] of required) {
      if (!val || !String(val).trim()) {
        setError(`${label} is required.`);
        return;
      }
    }

    if (isOtherChapter && !form.chapter_other.trim()) {
      setError("Please specify your chapter.");
      return;
    }

    await onSubmit({
      ...form,
      email: authEmail,
      phone: authPhone,
      application_category_id: Number(authCategoryId),
      sector_id: Number(form.sector_id),
      state_id: Number(form.state_id),
      local_government_id: Number(form.local_government_id),
      chapter_id: isOtherChapter ? null : form.chapter_id,
      chapter_other: isOtherChapter ? form.chapter_other : undefined,
      pro_pic: proPic,
      signature,
    });
  };

  return (
    <form className="space-y-8 pb-10" onSubmit={handleSubmit}>
      {/* Header */}
      <div className="border-b border-gray-100 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-900 rounded-xl">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Personal Details
            </h2>
            <p className="text-sm text-gray-500">
              All fields are compulsory unless stated otherwise.
            </p>
          </div>
        </div>
      </div>

      {/* Personal info */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Field
          label="Title"
          value={form.title}
          onChange={(v) => onChange("title", v)}
          disabled={busy}
          placeholder="Mr / Mrs / Miss / Dr"
          required
        />
        <Field
          label="Surname"
          value={form.surname}
          onChange={(v) => onChange("surname", v)}
          disabled={busy}
          required
        />
        <Field
          label="Other Names"
          value={form.other_names}
          onChange={(v) => onChange("other_names", v)}
          disabled={busy}
          required
        />

        <SelectField
          label="Gender"
          value={form.gender}
          onChange={(v) => onChange("gender", v)}
          options={GENDER_OPTIONS}
          disabled={busy}
          required
        />
        <LockedField
          label={upgradeToCategoryName ? "Current Grade" : "Application Category"}
          value={authCategoryName}
        />
        {upgradeToCategoryName && (
          <LockedField label="Upgrading To" value={upgradeToCategoryName} />
        )}
        <SelectField
          label="Professional Sector"
          value={form.sector_id}
          onChange={(v) => onChange("sector_id", v)}
          options={sectors}
          disabled={busy}
          required
        />

        {/* Chapter — with "Others" option */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-gray-700 uppercase">
            State of Residence <span className="text-red-500">*</span>
          </label>
          <select
            disabled={busy}
            className="w-full p-2.5 border rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100 transition-all"
            value={form.chapter_id}
            onChange={(e) => {
              onChange("chapter_id", e.target.value);
              if (e.target.value !== "__other__") onChange("chapter_other", "");
            }}
          >
            <option value="">Select Chapter</option>
            {chapters.map((c: any) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
          {isOtherChapter && (
            <input
              type="text"
              disabled={busy}
              placeholder="Please specify your chapter"
              className="w-full p-2.5 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 mt-2"
              value={form.chapter_other}
              onChange={(e) => onChange("chapter_other", e.target.value)}
            />
          )}
        </div>
      </div>

      {/* Contact info */}
      <div className="grid md:grid-cols-3 gap-6">
        <LockedField label="Email Address" value={authEmail} />
        <div className="space-y-1">
          <LockedField label="Phone Number" value={authPhone} />
          {!authPhone && (
            <p className="text-[11px] text-red-500 font-medium">
              No phone on file — please update your profile before submitting.
            </p>
          )}
        </div>
        <Field
          label="Date of Birth"
          type="date"
          value={form.date_of_birth}
          onChange={(v) => onChange("date_of_birth", v)}
          disabled={busy}
          required
        />
      </div>

      {/* Residency */}
      <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 space-y-6">
        <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
          <MapPin className="w-3 h-3" /> Residency & Location
        </h4>
        <div className="grid md:grid-cols-3 gap-6">
          <SelectField
            label="State of Origin"
            value={form.state_id}
            onChange={(v) => {
              onChange("state_id", v);
              onChange("local_government_id", "");
            }}
            options={states}
            disabled={busy}
            required
          />
          <SelectField
            label="Local Government (LGA)"
            value={form.local_government_id}
            onChange={(v) => onChange("local_government_id", v)}
            options={localGovernments}
            disabled={busy || !form.state_id}
            required
          />
          <Field
            label="Nationality"
            value={form.nationality}
            onChange={(v) => onChange("nationality", v)}
            disabled={busy}
            required
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 uppercase">
              Residential Address <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              disabled={busy}
              className="w-full p-3 border rounded-xl text-sm min-h-[80px] outline-none focus:ring-2 focus:ring-gray-100 transition-all"
              value={form.residential_address}
              onChange={(e) => onChange("residential_address", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 uppercase">
              Postal Address{" "}
              <span className="text-gray-400 font-normal normal-case">
                (optional)
              </span>
            </label>
            <textarea
              disabled={busy}
              className="w-full p-3 border rounded-xl text-sm min-h-[80px] outline-none focus:ring-2 focus:ring-gray-100 transition-all"
              value={form.postal_address}
              onChange={(e) => onChange("postal_address", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Uploads */}
      <div className="grid md:grid-cols-2 gap-6">
        <FileField
          label="Passport Photograph"
          hint="Passport-size, clear white background · JPG or PNG · Min 300×300px · Max 2MB · Auto-optimised if larger"
          onFile={handleProPicSelect}
          disabled={busy || !!compressing}
          compressing={compressing === "photo"}
          icon={<Camera className="w-4 h-4" />}
          previewUrl={proPicPreview}
          existingUrl={initialValues?.pro_pic_url}
          onClear={() => setProPic(null)}
        />
        <FileField
          label="Signature"
          hint="Plain white background, dark ink · JPG or PNG · Min 600×200px · Max 1MB · Auto-optimised if larger"
          onFile={handleSignatureSelect}
          disabled={busy || !!compressing}
          compressing={compressing === "signature"}
          icon={<PenTool className="w-4 h-4" />}
          previewUrl={signaturePreview}
          existingUrl={initialValues?.signature_url}
          onClear={() => setSignature(null)}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {!readOnly && (
        <div className="flex items-center justify-between pt-6 border-t border-gray-100">
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-lg text-xs font-medium">
            <Info className="w-4 h-4" />
            All fields marked <span className="text-red-500 mx-1">*</span> are
            required.
          </div>
          <button
            type="submit"
            disabled={busy}
            className="group flex items-center gap-3 px-8 py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg shadow-gray-200"
          >
            {busy ? "Processing..." : "Save & Continue"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      )}
    </form>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────────── */

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
}

function LockedField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase">
        {label}
      </label>
      <div className="w-full p-2.5 border border-gray-100 rounded-xl text-sm bg-gray-50 text-gray-400 flex items-center gap-2 select-none">
        <Lock className="w-3 h-3 text-gray-300 shrink-0" />
        <span className="truncate">{value || "—"}</span>
      </div>
      <p className="text-[10px] text-gray-300">Sourced from your profile</p>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  disabled,
  placeholder,
  required,
}: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        required={required}
        disabled={disabled}
        type={type}
        placeholder={placeholder}
        className="w-full p-2.5 border rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100 transition-all disabled:bg-gray-50"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: any[];
  disabled?: boolean;
  required?: boolean;
}

function SelectField({
  label,
  value,
  onChange,
  options,
  disabled,
  required,
}: SelectFieldProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        required={required}
        disabled={disabled}
        className="w-full p-2.5 border rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-gray-100 transition-all"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select {label}</option>
        {options.map((o: any) => (
          <option key={o.id} value={String(o.id)}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function FileField({
  label,
  hint,
  onFile,
  disabled,
  compressing,
  icon,
  required,
  previewUrl,
  existingUrl,
  onClear,
}: {
  label: string;
  hint?: string;
  onFile: (f: File | null) => void;
  disabled?: boolean;
  compressing?: boolean;
  icon: React.ReactNode;
  required?: boolean;
  previewUrl?: string | null;
  existingUrl?: string | null;
  onClear?: () => void;
}) {
  const activeUrl = previewUrl || existingUrl || null;
  const isNew = !!previewUrl;

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-gray-700 uppercase">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {activeUrl ? (
        <div className="rounded-xl overflow-hidden border border-gray-200 bg-white">
          <div className="relative">
            <img
              src={activeUrl}
              className="w-full h-40 object-contain bg-gray-50 p-2"
              alt={label}
            />
            <span
              className={`absolute top-2 left-2 text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${
                isNew ? "bg-blue-600 text-white" : "bg-gray-700 text-white"
              }`}
            >
              {isNew ? "New — Ready to upload" : "Currently on file"}
            </span>
            {isNew && onClear && (
              <button
                type="button"
                onClick={onClear}
                className="absolute top-2 right-2 p-1 bg-white rounded-full shadow text-red-500 hover:bg-red-50"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="border-t border-gray-100 px-3 py-2 flex items-center justify-between bg-gray-50">
            {compressing ? (
              <span className="text-[10px] text-indigo-500 font-medium flex items-center gap-1.5">
                <div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                Optimising image…
              </span>
            ) : (
              <>
                <span className="text-[10px] text-gray-400">
                  {isNew
                    ? "Will replace current on save"
                    : "Upload a new image to replace"}
                </span>
                <label className="cursor-pointer text-[10px] font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1">
                  {icon} Replace
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    className="sr-only"
                    disabled={disabled}
                    onChange={(e) => onFile(e.target.files?.[0] || null)}
                  />
                </label>
              </>
            )}
          </div>
        </div>
      ) : (
        <div
          className={`flex items-center gap-3 p-2 border-2 border-dashed rounded-xl transition-all ${
            compressing
              ? "border-indigo-300 bg-indigo-50"
              : "border-gray-200 hover:border-gray-400"
          }`}
        >
          <div
            className={`p-2 rounded-lg shrink-0 ${
              compressing
                ? "bg-indigo-100 text-indigo-500"
                : "bg-gray-100 text-gray-500"
            }`}
          >
            {compressing ? (
              <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              icon
            )}
          </div>
          {compressing ? (
            <span className="text-xs text-indigo-500 font-medium">
              Optimising image…
            </span>
          ) : (
            <input
              disabled={disabled}
              type="file"
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              className="text-xs text-gray-500 file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-gray-900 file:text-white cursor-pointer"
              onChange={(e) => onFile(e.target.files?.[0] || null)}
            />
          )}
        </div>
      )}

      {hint && <p className="text-[11px] text-gray-400 leading-snug">{hint}</p>}
    </div>
  );
}
